/* eslint-disable @typescript-eslint/ban-ts-comment */
/**
 * ModuleName: store/task
 * Description: 离线下载任务本地管理
 *
 * @author : lushaoqing
 * @date : 2023/03/03 17:00
 */

import { defineStore } from 'pinia'
import { reactive, ref, onMounted, computed, watch, nextTick } from 'vue'
import { store } from '@remote/store'
// import { useBaseStore } from '@remote/store/base'
import request from '@remote/api/adapter'
import { requestAnimationFrame } from '@remote/utils/base'
import {
  USER_DOWNLOAD,
  USER_DOWNLOAD_URL,
  TASK_TYPE,

} from '@remote/constant'
import iconLink from '@remote/assets/img/magnet.png'
import type { ITaskRes, IErrorRes } from '@remote/type'
import { logFactory } from '@remote/utils/logger'
import { safeJSONParse } from '@shared/utils'
// import { toast } from '@remote/utils/dialog'
import { pathResolve } from '@remote/api/adapter/helper'
import {
  getFileInfoBatch,
} from '@remote/api/adapter/shell-mqtt'
import LXTaskEventBus, { events } from '@remote/api/adapter/helper/batch-create-task/event-bus'

const log = logFactory('LocalTask')

type TPhase =
  'PHASE_TYPE_LOCAL' |
  'PHASE_TYPE_PENDING' |
  'PHASE_TYPE_RUNNING' |
  'PHASE_TYPE_ERROR' |
  'PHASE_TYPE_COMPLETE' |
  'PHASE_TYPE_PAUSE' |
  'PHASE_TYPE_PAUSE_LOADING' |
  'PHASE_TYPE_RUNNING_LOADING' |
  'PHASE_TYPE_LOCAL'

type TSpec = 'local' | 'delete' | 'pause' | 'running'
type TLocalTash = {
  kind: string
  id: string
  type: 'user#download-url'
  params: Partial<{
    url: string
    file_id: string
    spec: TSpec
    error: string
    is_deleted: string
  }>
  file_id: string
  file_name: string
  file_size: string
  phase: TPhase
  progress: number
  icon_link: string
  status: number
}

type TGetTasksDataParams = {
  limit: number
  page_token: string
  space: string
  type: 'done' | 'ing'
}

export async function getTasksData (params: TGetTasksDataParams): Promise<[IErrorRes, ITaskRes]> {
  const { limit, page_token, space, type } = params
  const url = '/drive/v1/tasks'
  const requestParams = {
    limit,
    page_token,
    space,
    filters: JSON.stringify({
      phase: { in: TASK_TYPE[type] },
      type: { in: `${USER_DOWNLOAD},${USER_DOWNLOAD_URL}` },
      bizGroup: { in: 'EMPTY' },
    }),
  }
  return request(url, requestParams)
}

export const LOCAL_STATUS_TASK_ALREADY_EXISTS = -6

export const LOCAL_STATUS_DELETE = -5
export const LOCAL_STATUS_SUBMITING = -4
export const LOCAL_STATUS_DOWNLOAD_TORRENT = -3
export const LOCAL_STATUS_PARSEING = -2
export const LOCAL_STATUS_PARSE_FAIL = -1
export const LOCAL_STATUS_PARSE_FINISH = 0
export const TASK_SPLIT_ID = 'TASK_SPLIT_ID'

const statusToNextAction = (status: number): ('restart' | 'pause') => {
  return status === LOCAL_STATUS_PARSE_FAIL ? 'restart' : 'pause'
}

export const createTask = (id: string, status = LOCAL_STATUS_SUBMITING, url = '', name = '', icon = ''): TLocalTash => {
  return {
    kind: 'drive/task',
    id: id,
    type: 'user#download-url',
    params: {
      url: url || id,
      file_id: id,
      spec: 'local',
      error: '',
    },
    file_id: '',
    file_name: name || url || id,
    file_size: '0',
    phase: 'PHASE_TYPE_LOCAL',
    progress: 0,
    icon_link: icon || iconLink,
    status: status,
  }
}
// 任务列表url映射任务详情
export const all = reactive<{ [url: string]: TLocalTash }>({})
// 任务已删除map
export const taskDeletedMap = reactive<{ [url: string]: boolean }>({})

const markDeleteMap: { [id: string]: boolean } = {}
const markHasQuery: { [path: string]: boolean } = {}

export const useTasks = defineStore('task', () => {
  const tasks = ref<string[]>([])
  const tasksIng = ref<string[]>([])
  const tasksDone = ref<string[]>([])
  const params = { space: '', type: 'ing', page_token: '', limit: 200 } as TGetTasksDataParams
  const hasMore = ref<boolean>(false)
  const loading = ref<boolean>(false)
  const foldTask = ref<{ ing: boolean; done: boolean }>({ ing: false, done: false })

  let prevType = 'ing'
  // const splitIndex = 0
  // 添加
  const setMap = (id: string, status = LOCAL_STATUS_SUBMITING, url = '', name = '', icon = '') => {
    const task = createTask(id, status, url, name, icon)
    all[task.id] = task
  }

  // 任务删除会移动至其他任务 local -> ing -> done
  const pop = (id?: string, taskType: 'ing' | 'done' | 'local' = 'local', forceDelete?: boolean) => {
    log('pop', id, taskType, forceDelete)
    let outTasks: typeof tasks | typeof tasksIng | typeof tasksDone
    let inTasks: typeof tasksIng | typeof tasksDone | null = null

    if (taskType === 'local') {
      outTasks = tasks
      inTasks = tasksIng
    } else if (taskType === 'ing') {
      outTasks = tasksIng
      inTasks = tasksDone
      console.log('markDeleteMap', taskType)
      if (id) markDeleteMap[id] = true
    } else {
      outTasks = tasksDone
      console.log('markDeleteMap', taskType)
      if (id) markDeleteMap[id] = true
    }

    const taskId = id || outTasks.value.pop()

    if (taskId && all[taskId]) {
      delete taskDeletedMap[taskId]
      delete markDeleteMap[taskId]
      const index = outTasks.value.findIndex(task => task === taskId)
      outTasks.value.splice(index, 1)
      // 针对本地任务非强制删除则进行状态扭转
      if (!forceDelete && inTasks) {
        console.log('task pop:', '针对本地任务非强制删除则进行状态扭转', id)
        if (id && !inTasks.value.includes(id)) {
          inTasks.value.unshift(id)
        }
      }
      return forceDelete ? nextTick(() => {
        if (all[taskId]) delete all[taskId]
      }) : Promise.resolve()
    }
    return Promise.resolve()
  }

  // 添加任务时任务已存在任务列表中的场景
  const push = (id: string, status = LOCAL_STATUS_SUBMITING, url = '', name = ''): string | void => {
    log('push', id, status, url, name, tasksDone.value.includes(id))
    markDeleteMap[url] = false
    delete taskDeletedMap[id]
    if (all[id]) {
      if (tasks.value.includes(id)) {
        pop(id).then(() => {
          const task = createTask(id)
          all[task.id] = task
          tasks.value.unshift(task.id)
        })
      } else if (tasksIng.value.includes(id) || tasksDone.value.includes(id)) {
        // 如果存在前缀添加一个local
        const localId = `local_${id}`
        const task = createTask(localId, status, url, all[id].file_name || name, all[id].icon_link)
        all[localId] = task
        tasks.value.unshift(localId)
        markDeleteMap[localId] = false
        return localId
      } else {
        tasks.value.unshift(id)
      }
    } else {
      const task = createTask(id, status, url, name)
      all[task.id] = task
      tasks.value.unshift(task.id)
    }
  }

  // 更新
  const update = (taskId: string, status: number, error = '', action?: 'pause' | 'restart' | 'delete' | 'recreate') => {
    log('update', taskId, status, error, action)
    if (action) LXTaskEventBus.trigger(events.update, { name: 'update', args: [taskId, status, error, action] })
    if (action === 'delete') {
      return pop(taskId)
    }
    const handler = (taskId) => {
      const prev = all[taskId]
      log('handler', prev)
      if (!prev) return console.error('task no exit in map', taskId, prev)
      // 区分本地任务和其他任务
      if (prev.phase === 'PHASE_TYPE_LOCAL') {
        if (prev && (!action || statusToNextAction(prev.status) === action)) {
          prev.params.error = error
          log('error', error)
          const next = Object.assign(prev, { status })
          all[taskId] = next
        }
      } else if (action && prev) {
        if (['restart', 'running'].includes(action)) {
          prev.params.error = ''
          prev.params.spec = 'running'
          prev.phase = 'PHASE_TYPE_RUNNING_LOADING'
          const next = Object.assign(prev, { status: 0 })
          all[taskId] = next
        } else {
          prev.params.error = ''
          prev.params.spec = 'pause'
          prev.phase = 'PHASE_TYPE_PAUSE_LOADING'
          const next = Object.assign(prev, { status: 0 })
          all[taskId] = next
        }
      }
    }
    // 全部暂停和全部开始操作
    if (taskId === 'all') {
      tasksIng.value.forEach(handler)
      tasks.value.forEach(handler)
    } else {
      handler(taskId)
    }
  }

  // const baseStore = useBaseStore()
  const target = computed(() => {
    return store.state.curDeviceRunner?.target
  })

  const handleGetTasksData = async () => {
    if (loading.value) return
    loading.value = true
    try {
      const [err, res] = await getTasksData(params)
      if (err) {
        if (res?.error === 'unauthenticated') {
          store.commit('update', { status: 0, loading: 0 })
        } else {
          // 修复设备切换无法重载下载任务列表
          store.commit('update', { loading: 0 })
        }
        return
      }

      // 已完成首次加载,不需要重复闪loading
      const firstLoadTasks = store.state.firstLoadTasks
      if (firstLoadTasks[params.type]) {
        firstLoadTasks[params.type] = false
        store.commit('update', { firstLoadTasks })
      }
      const pathMap = {}
      const tasks = Array.isArray(res.tasks) ? res.tasks : []
      const list = tasks.map(v => {
        const extData = safeJSONParse(v.ext_data) as { name: string; dir: string } || {}
        const id = v.params.url
        // @ts-ignore
        v.extData = extData
        if (params.type === 'done' && !taskDeletedMap[id]) {
          const path = pathResolve(extData.dir, extData.name)
          if (!markHasQuery[path]) pathMap[path] = id
          markHasQuery[path] = true
        }
        // @ts-ignore
        all[v.params.url] = v
        return id
      })

      const paths = Object.keys(pathMap)
      if (paths.length) getFileInfoBatch(paths).then(res => {
        if (res?.result?.data?.length) {
          res?.result?.data?.forEach((v) => {
            delete pathMap[v.path]
          })

          Object.values(pathMap).forEach((id: string) => {
            taskDeletedMap[id] = true
          })
        }
      })

      if (params.type === 'ing') tasksIng.value = list
      if (params.type === 'done') tasksDone.value = list
      // 记录上一次提交类型
      prevType = params.type

      // 重置参数为下次拉取数据做准备
      if (list.length < params.limit) {
        // 进行中的任务加载完毕，切换至已完成
        if (params.type === 'ing') {
          params.type = 'done'
          params.page_token = ''
          loading.value = false
          hasMore.value = true
        } else {
          params.page_token = ''
          loading.value = false
          hasMore.value = false
        }
      } else {
        loading.value = false
        hasMore.value = true
        params.page_token = res.next_page_token
      }
    } catch (err) {
      console.log('handleGetTasksData err:', err)
      loading.value = false
    }
  }

  const loopGetTasksDataUitllTasksDone = async () => {
    const getData = async (resolve) => {
      await handleGetTasksData()
      if (hasMore.value && prevType !== 'done') {
        requestAnimationFrame(getData.bind(null, resolve))
      } else {
        resolve()
      }
    }

    const loop = () => {
      return new Promise((resolve) => {
        getData(resolve)
      })
    }

    await loop()
  }

  const toggleFoldTask = (type: 'ing' | 'done') => {
    foldTask.value[type] = !foldTask.value[type]
    // 已完成任务未加载过，执行加载，避免折叠后无法加载已完成任务
    if (type === 'ing' && prevType !== 'done' && !tasksDone.value.length) {
      loopGetTasksDataUitllTasksDone()
    }
  }

  const refreshTaskList = async () => {
    const taskIngParams = { space: params.space, type: 'ing', page_token: '', limit: 200 } as TGetTasksDataParams
    const taskDoneParams = { space: params.space, type: 'done', page_token: '', limit: 200 } as TGetTasksDataParams
    await getTasksData(taskIngParams).then(res => {
      const [taskIngErr, taskIngRes] = res
      if (taskIngErr || !taskIngRes.tasks) return
      const list: string[] = []
      taskIngRes.tasks.forEach(v => {
        const id = v.params.url
        // @ts-ignore
        v.extData = safeJSONParse(v.ext_data) || {}
        // @ts-ignore
        all[v.params.url] = v
        // 标记删除
        if (!markDeleteMap[id] && !id?.includes('.torrent')) list.push(id)
      })
      tasksIng.value = list
    })
    await getTasksData(taskDoneParams).then(res => {
      const [taskDoneErr, taskDoneRes] = res
      if (taskDoneErr || !taskDoneRes.tasks) return
      const list: string[] = []
      const pathMap = {}
      taskDoneRes.tasks.forEach(v => {
        const extData = safeJSONParse(v.ext_data) as { name: string; dir: string } || {}
        const id = v.params.url
        // @ts-ignore
        v.extData = extData
        if (!taskDeletedMap[id]) {
          const path = pathResolve(extData.dir, extData.name)
          if (!markHasQuery[path]) pathMap[path] = id
          markHasQuery[path] = true
        }
        // @ts-ignore
        all[id] = v
        // 标记删除
        if (!markDeleteMap[id]) list.push(id)
      })

      const paths = Object.keys(pathMap)
      if (paths.length) getFileInfoBatch(paths).then(res => {
        if (res?.result?.data?.length) {
          res?.result?.data?.forEach((v) => {
            delete pathMap[v.path]
          })

          Object.values(pathMap).forEach((id: string) => {
            taskDeletedMap[id] = true
          })
        }
      })

      tasksDone.value = list
    })

    setTimeout(() => {
      requestAnimationFrame(refreshTaskList)
    }, 5000)
  }

  watch(target, async (val) => {
    if (val) {
      params.space = val
      handleGetTasksData()
        .then(refreshTaskList)
    }
  })

  onMounted(() => {
    if (target.value) {
      params.space = target.value
      handleGetTasksData()
        .then(refreshTaskList)
    }
  })

  return {
    tasks,
    tasksIng,
    tasksDone,
    hasMore,
    loading,
    foldTask,
    setMap,
    push,
    pop,
    update,
    handleGetTasksData,
    toggleFoldTask,
  }
})